/**
 * Thanh tab KÊNH đặt trên cùng màn Tổng quan. Mỗi kênh có bộ chỉ số theo dõi
 * khác nhau (Zalo cá nhân có danh bạ bạn bè; OA/Facebook/Web chat thì không),
 * nên báo cáo tách theo tab thay vì gộp một màn rồi lọc bằng ô chọn.
 */
import { LayoutGrid } from 'lucide-react'
import { CHANNEL_GROUPS, groupOfPlatform, type ChannelGroupId } from '@/lib/channel-groups'
import { useZaloAccounts } from '@/hooks/use-integrations'
import { cn } from '@/lib/utils'

/** Giá trị "mọi tài khoản" cho ô chọn tài khoản trong kênh. Báo cáo KHÔNG có tab gộp mọi kênh. */
export const ALL_SCOPE = '__all__'
export type KenhBaoCao = ChannelGroupId

/** Kênh nào có danh bạ liên hệ (bạn bè Zalo) để tính các chỉ số "liên hệ". */
export function kenhCoDanhBa(kenh: KenhBaoCao): boolean {
  return kenh === 'zalo_user'
}

export function nhanKenh(kenh: KenhBaoCao): string {
  return CHANNEL_GROUPS.find((g) => g.id === kenh)?.label ?? kenh
}

const KHOA_LUU = 'dashboard_kenh'
export function docKenhDaLuu(): KenhBaoCao {
  try {
    const v = localStorage.getItem(KHOA_LUU)
    if (v && CHANNEL_GROUPS.some((g) => g.id === v)) return v as KenhBaoCao
  } catch { /* riêng tư / bị chặn */ }
  return 'zalo_user'
}
export function luuKenh(k: KenhBaoCao): void {
  try { localStorage.setItem(KHOA_LUU, k) } catch { /* bỏ qua */ }
}

export function KenhTabs({ kenh, onChange }: { kenh: KenhBaoCao; onChange: (k: KenhBaoCao) => void }) {
  const { data: accounts = [] } = useZaloAccounts()
  const dem = (id: ChannelGroupId) => accounts.filter((a) => !a.isDisabled && groupOfPlatform(a.platform) === id).length
  const tabs: Array<{ id: KenhBaoCao; label: string; icon: typeof LayoutGrid; so: number | null }> =
    CHANNEL_GROUPS.map((g) => ({ id: g.id, label: g.id === 'other' ? 'Khác (Web chat…)' : g.label, icon: g.icon, so: dem(g.id) }))
  return (
    <div role="tablist" aria-label="Kênh báo cáo" className="flex w-full gap-1 overflow-x-auto pb-0.5">
      {tabs.map((t) => {
        const active = kenh === t.id
        const Icon = t.icon
        return (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(t.id)}
            className={cn(
              'flex shrink-0 items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm transition-colors',
              active
                ? 'border-primary bg-primary text-primary-foreground shadow-sm'
                : 'border-transparent bg-muted/60 text-muted-foreground hover:bg-accent hover:text-foreground',
              t.so === 0 && !active && 'opacity-60',
            )}
            title={t.so === 0 ? 'Chưa kết nối tài khoản nào ở kênh này' : undefined}
          >
            <Icon className="h-4 w-4" />
            <span className="font-medium">{t.label}</span>
            {t.so != null && (
              <span className={cn('rounded-full px-1.5 text-[10.5px] tabular-nums', active ? 'bg-primary-foreground/20' : 'bg-background/80')}>
                {t.so}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}
